import { Inject, Injectable, InjectionToken, NgZone, Optional } from '@angular/core';
import { BehaviorSubject, Subscription } from 'rxjs';

import { ApiService } from 'src/app/services/api/api.service';

import {
  ContractedMbpsPlan,
  downloadFinalRange,
  JITTER_MS_RANGE,
  pickInRange,
  PING_MS_RANGE,
  RandomFn,
  resolveContractedPlan,
  roundMetric,
  sampleDownloadDuring,
  sampleJitterDuring,
  samplePingDuring,
  sampleUploadDuring,
  SPEED_TEST_SCENARIO_TIMING,
  uploadFinalRange,
} from './speed-test-scenario.math';
import {
  INITIAL_SPEED_TEST_SNAPSHOT,
  isSpeedTestRunning,
  SpeedTestSnapshot,
} from './speed-test.types';

export const SPEED_TEST_SCENARIO_RANDOM = new InjectionToken<RandomFn>('SPEED_TEST_SCENARIO_RANDOM');

export const MISSING_PLAN_ERROR =
  'No se encontró un plan contratado con velocidades de bajada y subida.';
export const PLAN_FETCH_ERROR = 'No se pudo obtener el plan contratado. Intente nuevamente.';

@Injectable({
  providedIn: 'root',
})
export class SpeedTestScenarioService {
  private readonly snapshotSubject = new BehaviorSubject<SpeedTestSnapshot>({
    ...INITIAL_SPEED_TEST_SNAPSHOT,
  });
  readonly snapshot$ = this.snapshotSubject.asObservable();

  private readonly random: RandomFn;
  private timerId: ReturnType<typeof setTimeout> | null = null;
  private servicesSub?: Subscription;
  private runGeneration = 0;

  constructor(
    private readonly api: ApiService,
    private readonly ngZone: NgZone,
    @Optional() @Inject(SPEED_TEST_SCENARIO_RANDOM) random?: RandomFn,
  ) {
    this.random = random ?? (() => Math.random());
  }

  get snapshot(): SpeedTestSnapshot {
    return this.snapshotSubject.value;
  }

  start(): void {
    if (isSpeedTestRunning(this.snapshot.status)) {
      return;
    }

    this.stopWork();
    const generation = ++this.runGeneration;
    this.snapshotSubject.next({
      ...INITIAL_SPEED_TEST_SNAPSHOT,
      status: 'preparing',
    });

    this.servicesSub = this.api.customer.getInstitutionalServices().subscribe({
      next: (services) => {
        if (generation !== this.runGeneration) {
          return;
        }

        const plan = resolveContractedPlan(services);
        if (!plan) {
          this.fail(MISSING_PLAN_ERROR);
          return;
        }

        this.schedule(SPEED_TEST_SCENARIO_TIMING.preparingMs, () => this.beginPing(plan, generation));
      },
      error: () => {
        if (generation !== this.runGeneration) {
          return;
        }
        this.fail(PLAN_FETCH_ERROR);
      },
    });
  }

  abort(): void {
    if (!isSpeedTestRunning(this.snapshot.status)) {
      return;
    }

    this.stopWork();
    this.runGeneration += 1;
    this.snapshotSubject.next({
      ...this.snapshot,
      status: 'aborted',
      errorMessage: 'Prueba cancelada.',
    });
  }

  dispose(): void {
    this.stopWork();
    this.runGeneration += 1;
    this.snapshotSubject.next({ ...INITIAL_SPEED_TEST_SNAPSHOT });
  }

  private beginPing(plan: ContractedMbpsPlan, generation: number): void {
    if (generation !== this.runGeneration) {
      return;
    }

    const pingFinalMs = roundMetric(pickInRange(PING_MS_RANGE.min, PING_MS_RANGE.max, this.random));
    const jitterFinalMs = roundMetric(pickInRange(JITTER_MS_RANGE.min, JITTER_MS_RANGE.max, this.random));

    this.runTimedPhase(
      generation,
      SPEED_TEST_SCENARIO_TIMING.pingMs,
      (t) => {
        const done = t >= 1;
        this.patch({
          status: 'ping',
          pingMs: done ? pingFinalMs : roundMetric(samplePingDuring(t, pingFinalMs)),
          jitterMs: done ? jitterFinalMs : roundMetric(sampleJitterDuring(t, jitterFinalMs)),
          pingProgress: t,
        });
      },
      () => this.beginDownload(plan, pingFinalMs, jitterFinalMs, generation),
    );
  }

  private beginDownload(
    plan: ContractedMbpsPlan,
    pingFinalMs: number,
    jitterFinalMs: number,
    generation: number,
  ): void {
    if (generation !== this.runGeneration) {
      return;
    }

    const range = downloadFinalRange(plan.downloadMbps);
    const downloadFinalMbps = roundMetric(pickInRange(range.min, range.max, this.random));

    this.runTimedPhase(
      generation,
      SPEED_TEST_SCENARIO_TIMING.downloadMs,
      (t) => {
        const done = t >= 1;
        this.patch({
          status: 'download',
          pingMs: pingFinalMs,
          jitterMs: jitterFinalMs,
          pingProgress: 1,
          downloadMbps: done ? downloadFinalMbps : roundMetric(sampleDownloadDuring(t, plan.downloadMbps)),
          downloadProgress: t,
        });
      },
      () => this.beginUpload(plan, pingFinalMs, jitterFinalMs, downloadFinalMbps, generation),
    );
  }

  private beginUpload(
    plan: ContractedMbpsPlan,
    pingFinalMs: number,
    jitterFinalMs: number,
    downloadFinalMbps: number,
    generation: number,
  ): void {
    if (generation !== this.runGeneration) {
      return;
    }

    const range = uploadFinalRange(plan.uploadMbps);
    const uploadFinalMbps = roundMetric(pickInRange(range.min, range.max, this.random));

    this.runTimedPhase(
      generation,
      SPEED_TEST_SCENARIO_TIMING.uploadMs,
      (t) => {
        const done = t >= 1;
        this.patch({
          status: 'upload',
          pingMs: pingFinalMs,
          jitterMs: jitterFinalMs,
          pingProgress: 1,
          downloadMbps: downloadFinalMbps,
          downloadProgress: 1,
          uploadMbps: done ? uploadFinalMbps : roundMetric(sampleUploadDuring(t, plan.uploadMbps)),
          uploadProgress: t,
        });
      },
      () => this.complete(pingFinalMs, jitterFinalMs, downloadFinalMbps, uploadFinalMbps, generation),
    );
  }

  private complete(
    pingFinalMs: number,
    jitterFinalMs: number,
    downloadFinalMbps: number,
    uploadFinalMbps: number,
    generation: number,
  ): void {
    if (generation !== this.runGeneration) {
      return;
    }

    this.snapshotSubject.next({
      status: 'completed',
      downloadMbps: downloadFinalMbps,
      uploadMbps: uploadFinalMbps,
      pingMs: pingFinalMs,
      jitterMs: jitterFinalMs,
      downloadProgress: 1,
      uploadProgress: 1,
      pingProgress: 1,
      errorMessage: null,
    });
  }

  private runTimedPhase(
    generation: number,
    durationMs: number,
    apply: (t: number) => void,
    done: () => void,
  ): void {
    const ticks = Math.max(1, Math.round(durationMs / SPEED_TEST_SCENARIO_TIMING.tickMs));
    let i = 0;
    apply(0);

    const step = () => {
      if (generation !== this.runGeneration) {
        return;
      }

      i += 1;
      const t = Math.min(1, i / ticks);
      apply(t);

      if (i >= ticks) {
        this.timerId = null;
        done();
        return;
      }

      this.schedule(SPEED_TEST_SCENARIO_TIMING.tickMs, step);
    };

    this.schedule(SPEED_TEST_SCENARIO_TIMING.tickMs, step);
  }

  private schedule(ms: number, fn: () => void): void {
    this.timerId = setTimeout(() => this.ngZone.run(fn), ms);
  }

  private fail(errorMessage: string): void {
    this.stopWork();
    this.snapshotSubject.next({
      ...INITIAL_SPEED_TEST_SNAPSHOT,
      status: 'failed',
      errorMessage,
    });
  }

  private patch(partial: Partial<SpeedTestSnapshot>): void {
    this.snapshotSubject.next({
      ...this.snapshotSubject.value,
      ...partial,
    });
  }

  private stopWork(): void {
    this.servicesSub?.unsubscribe();
    this.servicesSub = undefined;
    if (this.timerId != null) {
      clearTimeout(this.timerId);
      this.timerId = null;
    }
  }
}
